import {
  isEntryLocked, isLive, linkKey, loggedByOccurrence, occurrencesBetween, projectColor,
  type Dataset, type GoogleCalendar, type Occurrence, type RepeatRule, type TimeEntry
} from "@otrack/domain";
import type { CSSProperties } from "react";
import type { GoogleView } from "./google";
import { canEdit, googleLinkId, googleOccurrences, googleSeriesKey, projectOf, recurrenceOf, type GoogleOccurrence } from "./google-events";

export const DAY_MINUTES = 24 * 60;

/** A Time Entry as drawn: the running Timer ends at now. */
export interface EntryItem {
  kind: "entry";
  key: string;
  entry: TimeEntry;
  start: number;
  end: number;
  projectId: string | null;
  taskId: string | null;
  title: string;
  running: boolean;
  locked: boolean;
  billable: boolean;
}

/** A Google event occurrence as the Calendar needs it. */
export interface GooglePlan {
  calendar: GoogleCalendar;
  occurrence: GoogleOccurrence;
  /** ontrack may move, change, and delete it. */
  editable: boolean;
  /** The series' rule as a Repeat; null when it doesn't repeat or says more than a Repeat can. */
  repeat: RepeatRule | null;
  /** Remembers a Project for a series ontrack can't write to. */
  seriesKey: string;
  location: string;
}

/** Something planned: an ontrack Event occurrence or a Google Calendar event. */
export interface PlanItem {
  kind: "event" | "google";
  key: string;
  start: number;
  end: number;
  allDay: boolean;
  title: string;
  notes: string;
  projectId: string | null;
  taskId: string | null;
  recurring: boolean;
  occurrence: Occurrence | null;
  google: GooglePlan | null;
  /** What logged Time Entries point back to. */
  linkId: string;
  linkOccurrence: number | null;
  logged: TimeEntry[];
}

export type CalItem = EntryItem | PlanItem;

export function isEntry(item: CalItem): item is EntryItem {
  return item.kind === "entry";
}

/** Everything to draw between `from` and `to`. */
export function collectItems(dataset: Dataset, google: GoogleView, from: number, to: number, now: number): { entries: EntryItem[]; plans: PlanItem[] } {
  const entries: EntryItem[] = Object.values(dataset.timeEntries)
    .filter((entry) => isLive(entry) && entry.start < to && (entry.end ?? now) > from)
    .map((entry) => ({
      kind: "entry",
      key: `entry:${entry.id}`,
      entry,
      start: entry.start,
      end: entry.end ?? Math.max(entry.start, now),
      projectId: entry.projectId,
      taskId: entry.taskId,
      title: entry.description,
      running: entry.end === null,
      locked: isEntryLocked(dataset, entry),
      billable: entry.billable
    }));

  const logged = loggedByOccurrence(dataset);
  const plans: PlanItem[] = occurrencesBetween(dataset, from, to).map((occurrence) => ({
    kind: "event",
    key: `event:${occurrence.key}`,
    start: occurrence.start,
    end: occurrence.end,
    allDay: occurrence.allDay,
    title: occurrence.event.title,
    notes: occurrence.event.notes,
    projectId: occurrence.event.projectId,
    taskId: occurrence.event.taskId,
    recurring: occurrence.recurring,
    occurrence,
    google: null,
    linkId: occurrence.seriesId,
    linkOccurrence: occurrence.occurrence,
    logged: logged.get(linkKey(occurrence.seriesId, occurrence.occurrence)) ?? []
  }));

  for (const calendar of google.calendars) {
    for (const item of googleOccurrences(calendar.id, google.events[calendar.id] ?? {}, from, to)) {
      const editable = canEdit(item.event, calendar);
      const seriesKey = googleSeriesKey(calendar.id, item.seriesId);
      const own = projectOf(item.event);
      const projectId = editable ? own.projectId : dataset.settings.googleProjects[seriesKey] ?? own.projectId;
      const project = projectId ? dataset.projects[projectId] : undefined;
      const live = project !== undefined && isLive(project);
      const linkId = googleLinkId(calendar.id, item.seriesId);
      const series = item.master ?? item.event;
      plans.push({
        kind: "google",
        key: `google:${item.key}`,
        start: item.start,
        end: item.end,
        allDay: item.allDay,
        title: item.event.summary?.trim() || "Busy",
        notes: item.event.description ?? "",
        projectId: live ? project.id : null,
        taskId: live && editable ? own.taskId : null,
        recurring: item.recurring,
        occurrence: null,
        google: { calendar, occurrence: item, editable, repeat: item.recurring ? recurrenceOf(series).repeat : null, seriesKey, location: item.event.location ?? "" },
        linkId,
        linkOccurrence: item.originalStart,
        logged: logged.get(linkKey(linkId, item.originalStart)) ?? []
      });
    }
  }
  plans.sort((a, b) => a.start - b.start || b.end - a.end);
  return { entries, plans };
}

/** Minutes since local midnight of `day`, by the wall clock. */
export function minuteOfDay(ts: number, day: number): number {
  if (ts <= day) return 0;
  const date = new Date(ts);
  const midnight = new Date(day);
  if (date.getFullYear() !== midnight.getFullYear() || date.getMonth() !== midnight.getMonth() || date.getDate() !== midnight.getDate()) return DAY_MINUTES;
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

/** The instant at `minute` past local midnight of `day`. */
export function atMinute(day: number, minute: number): number {
  const date = new Date(day);
  date.setHours(0, Math.round(minute), 0, 0);
  return date.getTime();
}

export interface Placed<T> {
  item: T;
  /** Minutes from the top of the day. */
  top: number;
  bottom: number;
  col: number;
  cols: number;
  /** Items that overlap one another, directly or through others, share a group. */
  group: number;
  /** The item continues from the day before or into the next. */
  fromBefore: boolean;
  intoAfter: boolean;
}

/** Minutes a very short item is given, room for a line of text. */
const LINE_MINUTES = 15;

/**
 * Places a day's items: each gets its vertical span and, where items overlap, a column within
 * its group of overlapping items, so overlapping plans sit side by side. With `stack`, only real
 * overlaps make columns: a short item takes a line's room only up to the next item, so entries
 * that follow one another closely stay in one column, however short.
 */
export function placeInDay<T extends { start: number; end: number; key: string }>(items: readonly T[], day: number, nextDay: number, stack = false): Array<Placed<T>> {
  const inDay = items
    .filter((item) => item.start < nextDay && (item.end > day || (item.end === item.start && item.start >= day)))
    .map((item) => {
      const top = minuteOfDay(Math.max(item.start, day), day);
      const end = item.end >= nextDay ? DAY_MINUTES : minuteOfDay(item.end, day);
      const bottom = stack ? Math.max(top, end) : Math.max(top + LINE_MINUTES, end);
      return { item, top, bottom: Math.min(DAY_MINUTES, bottom), col: 0, cols: 1, group: 0, fromBefore: item.start < day, intoAfter: item.end > nextDay };
    })
    .sort((a, b) => a.top - b.top || b.bottom - a.bottom || a.item.key.localeCompare(b.item.key));

  let group: typeof inDay = [];
  let groupEnd = -1;
  let groupIndex = 0;
  const columns: number[] = [];
  const close = () => {
    const cols = Math.max(1, columns.length);
    for (const placed of group) { placed.cols = cols; placed.group = groupIndex; }
    if (group.length) groupIndex += 1;
    group = [];
    columns.length = 0;
  };
  for (const placed of inDay) {
    if (placed.top >= groupEnd) { close(); groupEnd = -1; }
    let col = columns.findIndex((end) => end <= placed.top);
    if (col < 0) { col = columns.length; columns.push(placed.bottom); } else columns[col] = placed.bottom;
    placed.col = col;
    group.push(placed);
    groupEnd = Math.max(groupEnd, placed.bottom);
  }
  close();
  if (stack) {
    for (const placed of inDay) {
      const next = inDay.find((other) => other !== placed && other.top >= placed.bottom && other.top > placed.top);
      placed.bottom = Math.max(placed.bottom, Math.min(placed.top + LINE_MINUTES, next?.top ?? DAY_MINUTES, DAY_MINUTES));
    }
  }
  return inDay;
}

/** Time tracked inside a day; overnight entries count on each day for the part that falls there. */
export function trackedIn(entries: readonly EntryItem[], from: number, to: number): number {
  return entries.reduce((sum, item) => sum + Math.max(0, Math.min(item.end, to) - Math.max(item.start, from)), 0);
}

/** Timed planned time inside a day. All-day items do not count as hours. */
export function plannedIn(plans: readonly PlanItem[], from: number, to: number): number {
  return plans.filter((plan) => !plan.allDay).reduce((sum, plan) => sum + Math.max(0, Math.min(plan.end, to) - Math.max(plan.start, from)), 0);
}

/** CSS custom property carrying a Project's colour. */
export function colorOf(dataset: Dataset, projectId: string | null): string {
  const project = projectId ? dataset.projects[projectId] : undefined;
  return project ? `var(--ot-pc-${projectColor(project)})` : "var(--ot-pc-none)";
}

export function colorStyle(dataset: Dataset, projectId: string | null): CSSProperties {
  return { ["--pc" as string]: colorOf(dataset, projectId) };
}

/** Whether a plan can be moved, resized, and edited here: ontrack Events, and Google events ontrack may change. */
export function isEditablePlan(plan: Pick<PlanItem, "kind" | "google">): boolean {
  return plan.kind === "event" || (plan.google?.editable ?? false);
}

/** How a plan's card looks: in its Project Color, or neutral for a Google event without a Project. */
export function planTone(plan: Pick<PlanItem, "google" | "projectId">): "event" | "neutral" {
  return plan.google && !plan.projectId ? "neutral" : "event";
}

export const READ_ONLY = "Ontrack can't change this event: it's an invitation or on a read-only calendar. Alt-drag to copy it into your plans.";
